import { T } from '@tolgee/react';
import Head from 'next/head';
import Link from 'next/link';
import { ReactNode } from 'react';
import styled from 'styled-components';

import theme from '../../../assets/theme';
import { useMetrics } from '../../../hooks/useMetrics';
import { QUOTE_REQUEST_HREF } from './PlanOverviewSection';
import {
  ACCENT_GREEN,
  BORDER_WARM,
  CARD_CREAM,
  CONTENT_MAX_WIDTH,
  MOBILE_QUERY,
  MUTED_BODY,
  MUTED_LABEL,
  NARROW_QUERY,
  NAVY,
  PASTEL_PEACH,
  PASTEL_PURPLE,
  PASTEL_TAN,
  SERIF_FONT,
  TILE_TEXT,
} from './pricingTokens';

type CaseStudyRow = {
  id: string;
  stat: ReactNode;
  text: ReactNode;
  attribution: ReactNode;
  href: string;
};

const caseStudyRows: CaseStudyRow[] = [
  {
    id: 'green-valley',
    stat: <T keyName="Pricing.Header.GreenValley.Stat" defaultValue="20 pts" />,
    text: (
      <T
        keyName="Pricing.Header.GreenValley.Text"
        defaultValue="STAAR RLA passing, 69% → 89% —"
      />
    ),
    attribution: (
      <T
        keyName="Pricing.Header.GreenValley.Attribution"
        defaultValue="Green Valley Elementary, TX"
      />
    ),
    href: '/case-studies/curipod-case-study-green-valley',
  },
  {
    id: 'california-writing',
    stat: <T keyName="Pricing.Header.CaliforniaWriting.Stat" defaultValue="46%" />,
    text: (
      <T
        keyName="Pricing.Header.CaliforniaWriting.Text"
        defaultValue="CAASPP growth in upper elementary ELA —"
      />
    ),
    attribution: (
      <T
        keyName="Pricing.Header.CaliforniaWriting.Attribution"
        defaultValue="Southern California"
      />
    ),
    href: '/case-studies/california-writing-achievement',
  },
  {
    id: 'southern-ca',
    stat: <T keyName="Pricing.Header.SouthernCa.Stat" defaultValue="35%" />,
    text: (
      <T
        keyName="Pricing.Header.SouthernCa.Text"
        defaultValue="ELA growth, 51% → 69% meeting standards —"
      />
    ),
    attribution: (
      <T
        keyName="Pricing.Header.SouthernCa.Attribution"
        defaultValue="5th grade, Southern CA"
      />
    ),
    href: '/case-studies/curipod-case-study-southern-ca',
  },
  {
    id: 'sivells-bend',
    stat: <T keyName="Pricing.Header.SivellsBend.StatV2" defaultValue="C → A" />,
    text: (
      <T
        keyName="Pricing.Header.SivellsBend.TextV2"
        defaultValue="District accountability rating, from a C to an A in a single year —"
      />
    ),
    attribution: (
      <T
        keyName="Pricing.Header.SivellsBend.Attribution"
        defaultValue="Sivells Bend, TX"
      />
    ),
    href: '/case-studies/curipod-case-study-sivells-bend',
  },
];

/** One tile background per case study, in display order — matches the mock's
 *  four tiles exactly: three tints, then white/bordered for the last. */
const TILE_BACKGROUNDS: { background: string; border?: string }[] = [
  { background: PASTEL_PEACH },
  { background: PASTEL_TAN },
  { background: PASTEL_PURPLE },
  { background: theme.colors.white, border: BORDER_WARM },
];

const TrendUpIcon = () => (
  <svg
    width="26"
    height="26"
    viewBox="0 0 24 24"
    fill="none"
    stroke={ACCENT_GREEN}
    strokeWidth="2.6"
    strokeLinecap="round"
    strokeLinejoin="round"
    style={{ flexShrink: 0 }}
    aria-hidden="true"
  >
    <path d="M4 17 L10.5 10.5 L14 14 L20 7" />
    <path d="M14.5 7 L20 7 L20 12.5" />
  </svg>
);

/**
 * Pricing page header — design variant 2b: a plain "Pricing" title on the
 * left, a 2-column grid of pastel case-study tiles on the right (every tile
 * carries the growth-trend icon, not just the ones the mock marks — see the
 * PR discussion). Passed to `LandingPageLayout` as `customHeader` from both
 * `/pricing` and `/lang/[locale]/pricing`.
 *
 * Two things about the surrounding markup are load-bearing:
 *  - `LandingPageHeader`'s own container paints `/static/bground.svg` behind this
 *    (it never receives `hideBlobs`), so `Band` must be full width with an opaque
 *    background or that artwork shows at the edges.
 *  - `CssOverwriteWrapper` in the same file forces `p { font-weight: bold;
 *    line-height: 22px; font-size: 14px; }` onto anything rendered here, at
 *    (0,1,1) specificity. `Subtitle` uses `&&` to outrank it.
 */
export const PricingPageHeader = () => {
  const metrics = useMetrics(true);

  return (
    <>
      {/* This design's italic captions/attributions use Newsreader; the rest of
          the app is Nunito-only, so it's loaded here rather than app-wide. */}
      <Head>
        <link rel="preconnect" href="https://fonts.googleapis.com" />
        <link rel="preconnect" href="https://fonts.gstatic.com" crossOrigin="anonymous" />
        <link
          href="https://fonts.googleapis.com/css2?family=Newsreader:ital,wght@0,400;0,500;1,400&display=swap"
          rel="stylesheet"
        />
      </Head>
      <Band>
        <Inner>
          <Copy>
            <Title>
              <T keyName="Pricing.Header.Title" defaultValue="Pricing" />
            </Title>
            <Subtitle>
              <T
                keyName="Pricing.Header.Subtitle"
                defaultValue="Writing practice with feedback for your classroom, school and district."
              />
            </Subtitle>
            <Link href={QUOTE_REQUEST_HREF} passHref>
              <CtaButton
                onClick={() =>
                  metrics.logEvent('PricingPage.Header.RequestQuote.clicked')
                }
              >
                <T keyName="Pricing.Header.Cta" defaultValue="Request a quote" />
              </CtaButton>
            </Link>
          </Copy>

          <Evidence>
            <EvidenceEyebrow>
              <T
                keyName="Pricing.Header.CaseStudiesEyebrow"
                defaultValue="Measured outcomes from schools using Curipod"
              />
            </EvidenceEyebrow>
            <TileGrid>
              {caseStudyRows.map((row, index) => {
                const tile = TILE_BACKGROUNDS[index % TILE_BACKGROUNDS.length];
                return (
                  <Link key={row.id} href={row.href} passHref>
                    <Tile
                      $background={tile.background}
                      $border={tile.border}
                      onClick={() =>
                        metrics.logEvent('PricingPage.Header.CaseStudyClicked', {
                          caseStudy: row.id,
                        })
                      }
                    >
                      <TileStat>
                        {row.stat}
                        <TrendUpIcon />
                      </TileStat>
                      <TileText>
                        {row.text} {row.attribution}
                      </TileText>
                    </Tile>
                  </Link>
                );
              })}
            </TileGrid>
          </Evidence>
        </Inner>
      </Band>
    </>
  );
};

const Band = styled.header`
  width: 100%;
  background: ${CARD_CREAM};
`;

const Inner = styled.div`
  max-width: ${CONTENT_MAX_WIDTH};
  margin: 0 auto;
  padding: 80px 32px;
  display: grid;
  grid-template-columns: 0.85fr 1.15fr;
  gap: 64px;
  align-items: center;

  @media ${MOBILE_QUERY} {
    grid-template-columns: 1fr;
    gap: 40px;
    padding: 56px 24px;
  }

  @media ${NARROW_QUERY} {
    padding: 40px 20px;
  }
`;

const Copy = styled.div`
  display: flex;
  flex-direction: column;
  gap: 22px;
`;

const Title = styled.h1`
  margin: 0;
  font-size: 76px;
  line-height: 1;
  font-weight: 900;
  letter-spacing: -0.025em;
  color: ${NAVY};

  @media ${MOBILE_QUERY} {
    font-size: 58px;
  }

  @media ${NARROW_QUERY} {
    font-size: 44px;
  }
`;

const Subtitle = styled.p`
  && {
    margin: 0;
    max-width: 420px;
    font-size: 20px;
    line-height: 1.6;
    font-weight: 600;
    color: ${MUTED_BODY};
  }

  @media ${NARROW_QUERY} {
    && {
      font-size: 17px;
    }
  }
`;

const CtaButton = styled.a`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  height: 52px;
  width: fit-content;
  padding: 0 30px;
  margin-top: 4px;
  border-radius: 8px;
  background: ${theme.colors.orange};
  color: ${theme.colors.white};
  font-size: 16px;
  font-weight: 700;
  cursor: pointer;
  transition: background 0.2s ease;

  &:hover {
    background: ${theme.colors.orangeDark};
  }

  @media ${NARROW_QUERY} {
    width: 100%;
  }
`;

const Evidence = styled.div`
  display: flex;
  flex-direction: column;
  gap: 18px;
`;

const EvidenceEyebrow = styled.span`
  font-size: 12px;
  font-weight: 700;
  letter-spacing: 0.18em;
  text-transform: uppercase;
  color: ${MUTED_LABEL};
`;

const TileGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 18px;

  @media ${NARROW_QUERY} {
    grid-template-columns: 1fr;
  }
`;

const Tile = styled.a<{ $background: string; $border?: string }>`
  display: block;
  background: ${({ $background }) => $background};
  border: ${({ $border }) => ($border ? `1px solid ${$border}` : 'none')};
  border-radius: 18px;
  padding: 30px 30px 28px;
  cursor: pointer;
  transition: transform 0.15s ease;

  &:hover {
    transform: translateY(-2px);
  }

  @media ${NARROW_QUERY} {
    padding: 24px 24px 22px;
  }
`;

const TileStat = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  font-size: 44px;
  font-weight: 900;
  line-height: 1;
  color: ${NAVY};

  @media ${MOBILE_QUERY} {
    font-size: 36px;
  }
`;

const TileText = styled.div`
  font-family: ${SERIF_FONT};
  font-size: 18px;
  line-height: 1.45;
  color: ${TILE_TEXT};
  margin-top: 12px;

  @media ${NARROW_QUERY} {
    font-size: 16px;
  }
`;

export default PricingPageHeader;
